import { useState, useCallback } from "react";
import { extractErrorMessage } from "../utils/errorUtils";

export interface UseFormSubmissionOptions {
  onSuccess?: (data?: any) => void;
  onError?: (error: any) => void;
  successMessage?: string;
  autoHideSuccess?: number; // milliseconds
}

/**
 * Hook to manage form submission state and error handling
 */
export const useFormSubmission = (options: UseFormSubmissionOptions = {}) => {
  const { 
    onSuccess, 
    onError, 
    successMessage = "Operation completed successfully!",
    autoHideSuccess = 4000 
  } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const getErrorMessage = useCallback((error: any): string => {
    return extractErrorMessage(error);
  }, []);

  const handleSubmit = useCallback(async (
    submitFunction: () => Promise<any>
  ) => {
    try {
      setIsSubmitting(true);
      setSubmitError("");
      setShowValidationErrors(false);

      const result = await submitFunction();
      
      setSuccessMsg(successMessage);
      
      // Auto-hide success message
      if (autoHideSuccess > 0) {
        setTimeout(() => {
          setSuccessMsg("");
        }, autoHideSuccess);
      }

      onSuccess?.(result);
      
      return result;
    } catch (error) {
      console.error("Form submission error:", error);
      const errorMsg = getErrorMessage(error);
      setSubmitError(errorMsg);
      setShowValidationErrors(true);
      onError?.(error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [successMessage, autoHideSuccess, onSuccess, onError, getErrorMessage]);

  const clearMessages = useCallback(() => {
    setSubmitError("");
    setSuccessMsg("");
    setShowValidationErrors(false);
  }, []);

  return {
    isSubmitting,
    submitError,
    successMessage: successMsg,
    showValidationErrors,
    handleSubmit,
    clearMessages,
    setSuccessMessage: setSuccessMsg,
    setSubmitError,
  };
};
